'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  ANALYTICS_CONSENT_KEY, ANALYTICS_CONSENT_EVENT, sanitizeAnalyticsMessage, trackPageView, validAnalyticsMeasurementId,
} from '@/lib/analytics';

type Consent = 'granted' | 'denied' | null;
const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim();

export default function AnalyticsConsent() {
  const [consent, setConsent] = useState<Consent>(null);
  const [loaded, setLoaded] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const pathname = usePathname();
  const currentPath = useRef(pathname);
  currentPath.current = pathname;

  useEffect(() => {
    const read = () => {
      try {
        const saved = localStorage.getItem(ANALYTICS_CONSENT_KEY);
        setConsent(saved === 'granted' || saved === 'denied' ? saved : null);
      } catch { setConsent('denied'); }
      setLoaded(true);
    };
    read();
    window.addEventListener('storage', read);
    return () => window.removeEventListener('storage', read);
  }, []);

  useEffect(() => {
    if (!validAnalyticsMeasurementId(measurementId) || consent !== 'granted') return;
    const frame = document.createElement('iframe');
    frame.id = 'combine-consented-analytics';
    frame.title = 'Optional analytics';
    frame.hidden = true;
    frame.referrerPolicy = 'no-referrer';
    // The opaque origin prevents all access to the parent DOM, URL, cookies,
    // and storage. Communication is limited to a validated message protocol.
    frame.setAttribute('sandbox', 'allow-scripts');
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frame.contentWindow || event.origin !== 'null') return;
      if (!event.data || typeof event.data !== 'object' || event.data.event !== 'analytics_ready') return;
      window.combineAnalytics = input => {
        const message = sanitizeAnalyticsMessage(input);
        if (message) frame.contentWindow?.postMessage(message, '*');
      };
      trackPageView(currentPath.current);
    };
    window.addEventListener('message', onMessage);
    frame.onload = () => frame.contentWindow?.postMessage({ event: 'analytics_start' }, '*');
    frame.src = '/api/analytics-frame';
    document.body.appendChild(frame);
    return () => {
      delete window.combineAnalytics;
      window.removeEventListener('message', onMessage);
      frame.remove();
    };
  }, [consent]);

  useEffect(() => { trackPageView(pathname); }, [pathname]);

  const choose = (value: Exclude<Consent, null>) => {
    try { localStorage.setItem(ANALYTICS_CONSENT_KEY, value); } catch { return; }
    if (value === 'denied') {
      for (const cookie of document.cookie.split(';')) {
        const name = cookie.split('=')[0].trim();
        if (!name.startsWith('_ga')) continue;
        const parts = location.hostname.split('.');
        document.cookie = `${name}=; Max-Age=0; path=/`;
        for (let i = 0; i < parts.length - 1; i++) document.cookie = `${name}=; Max-Age=0; path=/; domain=.${parts.slice(i).join('.')}`;
      }
    }
    setConsent(value); setShowChoices(false);
    window.dispatchEvent(new Event(ANALYTICS_CONSENT_EVENT));
  };

  if (!measurementId || !loaded) return null;
  if (consent && !showChoices) return <button className="fixed bottom-3 left-3 z-50 rounded border bg-white px-3 py-2 text-xs text-gray-800 shadow dark:bg-gray-900 dark:text-gray-100" onClick={() => setShowChoices(true)}>Analytics settings</button>;
  return (
    <section aria-label="Analytics consent" className="fixed bottom-3 left-3 right-3 z-50 mx-auto max-w-xl rounded-xl border border-gray-300 bg-white p-4 text-gray-900 shadow-lg dark:bg-gray-900 dark:text-gray-100">
      <p className="text-sm">Allow optional cookie-free Google Analytics to count page visits and feature use? Activity data and athlete IDs are excluded. You can change this choice at any time.</p>
      <div className="mt-3 flex gap-3">
        <button onClick={() => choose('denied')} className="rounded border px-4 py-2 text-sm">Decline</button>
        <button onClick={() => choose('granted')} className="rounded border px-4 py-2 text-sm">Allow analytics</button>
      </div>
    </section>
  );
}
